import React, { useMemo, useState } from 'react';
import { Copy, Check, ChevronDown, ChevronUp, AlertTriangle, Info, Download } from 'lucide-react';

/**
 * Writer-facing renderer for the Script Normalizer recipe output bundle.
 * Spec v3 §7. Expects an output of shape:
 *   { type: 'json', content: {
 *       polished_script, enhanced_script,
 *       cast_bible: [{name, role, visual_signature, inferred_fields}],
 *       costume_bible: [{name, worn_by[], details, inferred_fields}],
 *       corner_bible: [{name, description, inferred_fields}],
 *       inferences_summary: [string],
 *       warnings: [string]
 *   }}
 *
 * Layout on desktop: polished script (left, primary) + CCC bibles (right tabs).
 * On mobile: stacked cards. Enhanced script + inferences/warnings below.
 */
const ScriptNormalizerOutput = ({ bundle }) => {
  const [enhancedOpen, setEnhancedOpen] = useState(false);
  const [activeBible, setActiveBible] = useState('cast');
  const [copied, setCopied] = useState(null);

  const {
    polished_script = '',
    enhanced_script = '',
    cast_bible = [],
    costume_bible = [],
    corner_bible = [],
    inferences_summary = [],
    warnings = [],
  } = bundle || {};

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1600);
  };

  const downloadScript = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeItems = useMemo(() => {
    if (activeBible === 'cast') return cast_bible;
    if (activeBible === 'costume') return costume_bible;
    return corner_bible;
  }, [activeBible, cast_bible, costume_bible, corner_bible]);

  const bibleTabs = [
    { key: 'cast', label: 'Cast', count: cast_bible.length },
    { key: 'costume', label: 'Costumes', count: costume_bible.length },
    { key: 'corner', label: 'Locations', count: corner_bible.length },
  ];

  return (
    <div className="space-y-4" data-testid="script-normalizer-output">
      {/* Warnings banner */}
      {warnings.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="sn-warnings">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-amber-200 mb-1">Things to review</div>
              <ul className="text-[11px] text-amber-100/80 space-y-1 list-disc list-inside">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Two-column: polished script | CCC bibles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Polished Script */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
             data-testid="sn-polished-script">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Polished Script</span>
            <div className="flex items-center gap-1">
              <button onClick={() => copyToClipboard(polished_script, 'polished')}
                      className="p-1.5 hover:bg-white/10 rounded transition-colors"
                      title="Copy polished script"
                      data-testid="sn-copy-polished">
                {copied === 'polished' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-white/50" />}
              </button>
              <button onClick={() => downloadScript(polished_script, 'polished_script.txt')}
                      className="p-1.5 hover:bg-white/10 rounded transition-colors"
                      title="Download polished script"
                      data-testid="sn-download-polished">
                <Download className="w-3.5 h-3.5 text-white/50" />
              </button>
            </div>
          </div>
          <pre className="p-3 text-[12px] leading-relaxed whitespace-pre-wrap font-mono text-white/80 max-h-[480px] overflow-y-auto">
            {polished_script}
          </pre>
        </div>

        {/* CCC Bibles */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
             data-testid="sn-ccc-bibles">
          <div className="flex border-b border-white/5">
            {bibleTabs.map(t => (
              <button key={t.key} onClick={() => setActiveBible(t.key)}
                      className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-wider transition-colors ${activeBible === t.key ? 'bg-ai-accent/10 text-ai-accent border-b-2 border-ai-accent' : 'text-white/50 hover:text-white/80'}`}
                      data-testid={`sn-bible-tab-${t.key}`}>
                {t.label} <span className="text-white/40">({t.count})</span>
              </button>
            ))}
          </div>
          <div className="p-3 space-y-3 max-h-[480px] overflow-y-auto" data-testid={`sn-bible-panel-${activeBible}`}>
            {activeItems.length === 0 ? (
              <div className="text-[11px] text-white/40 italic py-6 text-center">No entries extracted.</div>
            ) : activeItems.map((item, i) => (
              <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`sn-bible-item-${activeBible}-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-white">{item.name}</span>
                  {item.inferred_fields && item.inferred_fields.length > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 flex-shrink-0" title={`Inferred: ${item.inferred_fields.join(', ')}`}>
                      {item.inferred_fields.length} inferred
                    </span>
                  )}
                </div>
                {activeBible === 'cast' && (
                  <>
                    {item.role && <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">{item.role}</div>}
                    <div className="text-[11px] text-white/70 leading-relaxed">{item.visual_signature}</div>
                  </>
                )}
                {activeBible === 'costume' && (
                  <>
                    {item.worn_by && item.worn_by.length > 0 && (
                      <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">Worn by: {item.worn_by.join(', ')}</div>
                    )}
                    <div className="text-[11px] text-white/70 leading-relaxed">{item.details}</div>
                  </>
                )}
                {activeBible === 'corner' && (
                  <div className="text-[11px] text-white/70 leading-relaxed">{item.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Script — collapsible */}
      {enhanced_script && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
             data-testid="sn-enhanced-container">
          <button onClick={() => setEnhancedOpen(!enhancedOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 transition-colors"
                  data-testid="sn-toggle-enhanced">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Enhanced Script <span className="text-white/30 normal-case text-[10px] ml-1">(atmosphere + mood, reference only)</span></span>
            <div className="flex items-center gap-2">
              {enhancedOpen && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); copyToClipboard(enhanced_script, 'enhanced'); }}
                          className="p-1 hover:bg-white/10 rounded transition-colors"
                          title="Copy enhanced script"
                          data-testid="sn-copy-enhanced">
                    {copied === 'enhanced' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-white/50" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); downloadScript(enhanced_script, 'enhanced_script.txt'); }}
                          className="p-1 hover:bg-white/10 rounded transition-colors"
                          title="Download enhanced script"
                          data-testid="sn-download-enhanced">
                    <Download className="w-3.5 h-3.5 text-white/50" />
                  </button>
                </>
              )}
              {enhancedOpen ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
            </div>
          </button>
          {enhancedOpen && (
            <pre className="p-3 text-[12px] leading-relaxed whitespace-pre-wrap font-mono text-white/80 max-h-[480px] overflow-y-auto border-t border-white/5"
                 data-testid="sn-enhanced-body">
              {enhanced_script}
            </pre>
          )}
        </div>
      )}

      {/* Inferences summary — subtle footer */}
      {inferences_summary.length > 0 && (
        <details className="rounded-lg border border-white/5 bg-white/[0.02]" data-testid="sn-inferences-container">
          <summary className="px-3 py-2 text-[11px] text-white/50 cursor-pointer hover:text-white/80 flex items-center gap-2" data-testid="sn-toggle-inferences">
            <Info className="w-3.5 h-3.5" />
            What we inferred ({inferences_summary.length})
          </summary>
          <ul className="px-6 pb-3 pt-1 text-[11px] text-white/60 space-y-1 list-disc">
            {inferences_summary.map((s, i) => <li key={i} data-testid={`sn-inference-${i}`}>{s}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
};

export default ScriptNormalizerOutput;
